import axios, {AxiosError, AxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders} from "axios";
import {environment} from "../../environments/environment";

export const Api = axios.create({baseURL: environment.baseURL});

export default Api;

const HUB_BACK_URL = process.env.REACT_APP_HUB_BACK_URL;
const REFRESH_PATH = "/api/account/token/refresh/";

function parseJwt(token?: string | null): any | null {
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    try {
        return JSON.parse(window.atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    } catch {
        return null;
    }
}

function proximoDeExpirar(token?: string | null, prazoDeProximidade = 60 * 5): boolean {
    const payload = parseJwt(token);
    if (!payload || typeof payload.exp !== "number") return false;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp - now <= prazoDeProximidade;
}

let isRefreshing = false as boolean;
let refreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
    const refreshToken = sessionStorage.getItem("refreshToken");
    if (!refreshToken) return null;

    //Reinstanciar componente para evitar recursão
    const client = axios.create({baseURL: HUB_BACK_URL});

    try {
        const config: AxiosRequestConfig = {
            url: REFRESH_PATH,
            method: "post",
            data: { refresh: refreshToken },
            headers: { "Content-Type": "application/json" },
        };

        const response: AxiosResponse<any> = await client.request(config);
        const newAccess = response.data?.access;
        const newRefresh = response.data?.refresh;

        if (typeof newAccess === "string") {
            sessionStorage.setItem("token", newAccess);
        }
        if (typeof newRefresh === "string") {
            sessionStorage.setItem("refreshToken", newRefresh);
        }
        return typeof newAccess === "string" ? newAccess : null;
    } catch (e) {
        return null;
    }
}

function handleAuthFailure() {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refreshToken");
}

Api.interceptors.request.use((request: AxiosRequestConfig) => {
    let token = sessionStorage.getItem("token");

    if (token == undefined) {
        return request;
    }

    if (proximoDeExpirar(token) && !isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshToken().finally(() => {
            isRefreshing = false;
        });
    }

    if (refreshPromise) {
        return refreshPromise.then((newToken) => {
            const tk = newToken || sessionStorage.getItem("token");
            if (!request.headers) {
                request.headers = { Authorization: `Bearer ${tk}` };
            } else {
                (request.headers as RawAxiosRequestHeaders)["Authorization"] = `Bearer ${tk}`;
            }
            return request;
        });
    }



    if (!request.headers) {
        request.headers = { Authorization: `Bearer ${token}` };
    } else {
        (request.headers as RawAxiosRequestHeaders)["Authorization"] = `Bearer ${token}`;
    }

    return request;
}, err => {
    return Promise.reject(err);
});

Api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest: any = error.config || {};
        const status = error.response?.status;

        const isRefreshCall = (originalRequest?.url || "").includes(REFRESH_PATH);

        if ((status === 401 || status === 403) && !originalRequest._retry && !isRefreshCall) {
            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;
                refreshPromise = refreshToken().finally(() => {
                    isRefreshing = false;
                });
            }

            const newAccess = await (refreshPromise || Promise.resolve(null));
            if (newAccess) {
                // Update header and retry
                originalRequest.headers = originalRequest.headers || {};
                (originalRequest.headers as any)["Authorization"] = `Bearer ${newAccess}`;
                return Api.request(originalRequest);
            } else {
                handleAuthFailure();
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);
